import { Link } from 'react-router-dom';
import { useMemo, useState } from "react";
import { GenericCatalogTable } from "../../../components/generic-catalog-table";
import { ScrollReveal } from "../../../components/scroll-reveal";
import {
  DPDPA_CONTROLS,
  DPDPA_CROSSMAP,
  DPDPA_DOMAINS,
  DPDPA_PENALTY_TIERS,
} from "../../../data/dpdpa-controls";
import { PRIVACY_TECH_CATEGORIES, RECOMMENDED_STACKS } from "../../../data/dpdpa-privacy-tech";
import { cn } from "../../../lib/utils";

function pillClass(tone) {
  const base = "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold border border-border-subtle";
  if (tone === "red") return `${base} bg-[rgba(239,68,68,0.15)] text-[color:var(--accent-critical)]`;
  if (tone === "amber") return `${base} bg-[rgba(245,158,11,0.12)] text-[color:var(--accent-amber)]`;
  if (tone === "green") return `${base} bg-[rgba(16,185,129,0.12)] text-[color:var(--accent-compliance)]`;
  if (tone === "blue") return `${base} bg-[rgba(59,130,246,0.12)] text-[color:var(--accent-blue)]`;
  return `${base} bg-white/5 text-text-secondary`;
}

function penaltyTone(p) {
  if (p.includes("250")) return "red";
  if (p.includes("200")) return "amber";
  if (p.includes("150")) return "blue";
  if (p.includes("50")) return "neutral";
  return "neutral";
}

function automationTone(a) {
  if (a === "High") return "green";
  if (a === "Medium") return "amber";
  return "neutral";
}

export default function DpdpaFramework() {
  const [activeDomain, setActiveDomain] = useState(null);

  const filteredControls = useMemo(() => {
    if (!activeDomain) return DPDPA_CONTROLS;
    return DPDPA_CONTROLS.filter((c) => c.domain === activeDomain);
  }, [activeDomain]);

  const columns = useMemo(
    () => [
      { accessorKey: "controlId", header: "Control ID", size: 140 },
      { accessorKey: "domain", header: "Domain", size: 180 },
      { accessorKey: "dpdpaSection", header: "DPDPA Section", size: 130 },
      { accessorKey: "controlRequirement", header: "Control Requirement", size: 320 },
      { accessorKey: "implementationStandard", header: "Implementation Standard", size: 360 },
      { accessorKey: "evidence", header: "Evidence", size: 240 },
      { accessorKey: "owner", header: "Owner", size: 120 },
      { accessorKey: "frequency", header: "Frequency", size: 140 },
      { accessorKey: "technologyEnablers", header: "Technology Enablers", size: 240 },
      { accessorKey: "auditMethod", header: "Audit Method", size: 200 },
      { accessorKey: "commonGaps", header: "Common Gaps", size: 240 },
      {
        accessorKey: "penaltyRisk",
        header: "Penalty Risk",
        size: 140,
        cell: ({ getValue }) => {
          const v = String(getValue());
          return <span className={pillClass(penaltyTone(v))}>{v}</span>;
        },
      },
      {
        accessorKey: "automation",
        header: "Automation",
        size: 140,
        cell: ({ getValue }) => {
          const v = String(getValue());
          return <span className={pillClass(automationTone(v))}>{v}</span>;
        },
      },
    ],
    [],
  );

  return (
    <div>
      <section className="relative overflow-hidden py-16" style={{ background: "var(--gradient-hero)" }}>
        <div className="pointer-events-none absolute inset-0">
          <div className="aurora" />
          <div className="noise" />
        </div>

        <div className="container-ivalue relative">
          <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
            Indian Regulatory Frameworks / DPDPA
          </div>

          <h1 className="mt-4 text-balance text-4xl font-extrabold tracking-[-0.03em] text-text-primary md:text-6xl">
            Digital Personal Data
            <br />
            Protection Act, 2023
          </h1>

          <p className="mt-5 max-w-3xl text-lg leading-relaxed text-text-secondary">
            India&apos;s landmark privacy legislation establishing rights for Data Principals and obligations for Data Fiduciaries across all sectors.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <a
              href="#dpdpa-catalog"
              className="focus-ring sheen inline-flex h-12 items-center justify-center rounded-xl bg-gradient-to-r from-[#10B981] to-[#059669] px-5 text-sm font-semibold text-white transition-transform hover:scale-[1.02] active:scale-[0.98]"
            >
              Download DPDPA Control Catalog
            </a>
            <a
              href="#privacy-tech"
              className="focus-ring inline-flex h-12 items-center justify-center rounded-xl border border-border-subtle bg-white/5 px-5 text-sm font-semibold text-text-primary transition-colors hover:bg-white/10"
            >
              View Privacy Tech Stack
            </a>
            <Link
              to="/assessment"
              className="focus-ring inline-flex h-12 items-center justify-center rounded-xl px-5 text-sm font-semibold text-[color:var(--text-link)] transition-colors hover:text-text-primary"
            >
              Assessment →
            </Link>
          </div>

          <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { k: "Chapters", v: "8" },
              { k: "Sections", v: "44" },
              { k: "Controls", v: "85+" },
              { k: "Penalty", v: "₹250Cr" },
            ].map((s) => (
              <div key={s.k} className="glass-card sheen p-5">
                <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">{s.k}</div>
                <div className="mt-2 font-[family:var(--font-space-grotesk)] text-3xl font-semibold text-text-primary">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-secondary py-20">
        <div className="container-ivalue">
          <ScrollReveal>
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="glass-card p-6">
                <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Legislative context
                </div>
                <p className="mt-4 text-sm leading-relaxed text-text-secondary">
                  The Digital Personal Data Protection Act, 2023 (DPDPA) is India&apos;s comprehensive data protection law establishing a framework for processing digital personal data while recognizing both rights of individuals and legitimate needs of organizations.
                </p>
                <div className="mt-5 space-y-2">
                  {[
                    "Lawfulness, fairness, and transparency",
                    "Purpose limitation — collect only for specified purposes",
                    "Data minimization — collect only what is necessary",
                    "Accuracy — keep data accurate and up-to-date",
                    "Storage limitation — retain only as long as necessary",
                    "Security — implement appropriate safeguards",
                    "Accountability — demonstrate compliance",
                  ].map((x) => (
                    <div key={x} className="rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary">
                      {x}
                    </div>
                  ))}
                </div>
                <div className="mt-5 grid gap-2 sm:grid-cols-2">
                  <div className="rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary">
                    Effective Date: Phased implementation from 2024
                  </div>
                  <div className="rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary">
                    Enforcement: Data Protection Board of India (DPBI)
                  </div>
                </div>
              </div>

              <div className="glass-card p-6">
                <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">Key statistics</div>
                <div className="mt-4 space-y-2">
                  {[
                    "8 Chapters",
                    "44 Sections",
                    "85+ Implementation Controls",
                    "₹250 Crore Maximum Penalty",
                    "12–18 Month Implementation Window",
                  ].map((x) => (
                    <div key={x} className="rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary">
                      {x}
                    </div>
                  ))}
                </div>

                <div className="mt-6 rounded-2xl border border-border-subtle bg-white/5 p-5">
                  <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">Applicability</div>
                  <div className="mt-3 space-y-2 text-sm text-text-secondary">
                    {[
                      "All Data Fiduciaries processing digital personal data in India",
                      "Processing outside India if for offering goods/services in India",
                      "Significant Data Fiduciaries (SDF) with enhanced obligations",
                    ].map((x) => (
                      <div key={x} className="rounded-xl border border-border-subtle bg-white/5 px-3 py-2">
                        {x}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="bg-background-primary py-20" id="stakeholders">
        <div className="container-ivalue">
          <ScrollReveal>
            <div className="glass-card p-6">
              <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                DPDPA stakeholder ecosystem
              </div>
              <div className="mt-6 grid gap-4 lg:grid-cols-2">
                <div className="rounded-2xl border border-border-subtle bg-white/5 p-5">
                  <div className="text-lg font-semibold text-text-primary">Data Principal</div>
                  <div className="mt-2 text-sm text-text-secondary">Individual whose personal data is being processed</div>
                  <div className="mt-4 space-y-2">
                    {[
                      "Right to information",
                      "Right to correction/erasure",
                      "Right to grievance redressal",
                      "Right to nominate",
                    ].map((x) => (
                      <div key={x} className="rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary">
                        {x}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl border border-border-subtle bg-white/5 p-5">
                  <div className="text-lg font-semibold text-text-primary">Data Fiduciary</div>
                  <div className="mt-2 text-sm text-text-secondary">Entity that determines purpose and means of processing</div>
                  <div className="mt-4 space-y-2">
                    {[
                      "Lawful processing",
                      "Purpose limitation",
                      "Notice and consent",
                      "Data accuracy",
                      "Security safeguards",
                      "Breach notification",
                    ].map((x) => (
                      <div key={x} className="rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary">
                        {x}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl border border-border-subtle bg-white/5 p-5">
                  <div className="text-lg font-semibold text-text-primary">Data Processor</div>
                  <div className="mt-2 text-sm text-text-secondary">Entity processing on behalf of Data Fiduciary</div>
                  <div className="mt-4 rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary">
                    Contractual obligations from Data Fiduciary
                  </div>
                </div>

                <div className="rounded-2xl border border-border-subtle bg-white/5 p-5">
                  <div className="text-lg font-semibold text-text-primary">Significant Data Fiduciary (SDF)</div>
                  <div className="mt-2 text-sm text-text-secondary">Large-scale fiduciary with enhanced obligations</div>
                  <div className="mt-4 space-y-2">
                    {[
                      "Appoint DPO",
                      "Appoint independent data auditor",
                      "Conduct DPIA",
                      "Enhanced reporting to DPBI",
                    ].map((x) => (
                      <div key={x} className="rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary">
                        {x}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="bg-background-secondary py-20" id="domains">
        <div className="container-ivalue">
          <ScrollReveal>
            <div>
              <h2 className="text-3xl font-extrabold tracking-[-0.02em] text-text-primary md:text-5xl">10 control domains</h2>
              <p className="mt-3 max-w-3xl text-lg text-text-secondary">Jump into a domain and filter the catalog below.</p>
            </div>
          </ScrollReveal>

          <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {DPDPA_DOMAINS.map((d) => (
              <button
                key={d.key}
                type="button"
                onClick={() => {
                  const next = d.title;
                  setActiveDomain(next);
                  const el = document.getElementById("dpdpa-catalog");
                  el?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className={cn(
                  "text-left glass-card sheen p-5 transition-colors hover:bg-white/10",
                  activeDomain === d.title ? "ring-1 ring-white/15" : "",
                )}
              >
                <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">{d.title}</div>
                <div className="mt-2 text-sm text-text-secondary">{d.controlCount} controls</div>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-primary py-20" id="dpdpa-catalog">
        <div className="container-ivalue">
          <ScrollReveal>
            <div>
              <h2 className="text-3xl font-extrabold tracking-[-0.02em] text-text-primary md:text-5xl">DPDPA control catalog</h2>
              <p className="mt-3 max-w-3xl text-lg text-text-secondary">
                13-column implementation catalog: requirements, evidence, owners, frequency, technology enablers, audit method, common gaps, penalty risk, and automation.
              </p>
            </div>
          </ScrollReveal>

          <div className="mt-10">
            <GenericCatalogTable
              data={filteredControls}
              columns={columns}
              csvColumns={[
                { header: "Control ID", accessorKey: "controlId" },
                { header: "Domain", accessorKey: "domain" },
                { header: "DPDPA Section", accessorKey: "dpdpaSection" },
                { header: "Control Requirement", accessorKey: "controlRequirement" },
                { header: "Implementation Standard", accessorKey: "implementationStandard" },
                { header: "Evidence", accessorKey: "evidence" },
                { header: "Owner", accessorKey: "owner" },
                { header: "Frequency", accessorKey: "frequency" },
                { header: "Technology Enablers", accessorKey: "technologyEnablers" },
                { header: "Audit Method", accessorKey: "auditMethod" },
                { header: "Common Gaps", accessorKey: "commonGaps" },
                { header: "Penalty Risk", accessorKey: "penaltyRisk" },
                { header: "Automation", accessorKey: "automation" },
              ]}
            />
          </div>
        </div>
      </section>

      <section className="bg-background-secondary py-20" id="penalties">
        <div className="container-ivalue">
          <ScrollReveal>
            <div className="glass-card p-6">
              <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">DPDPA penalty structure</div>
              <div className="mt-6 overflow-x-auto">
                <table className="min-w-[780px] w-full border-separate border-spacing-0">
                  <thead>
                    <tr>
                      {[
                        "Tier",
                        "Maximum Penalty",
                        "Key Violations",
                      ].map((h) => (
                        <th key={h} className="border-b border-border-subtle px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {DPDPA_PENALTY_TIERS.map((t) => (
                      <tr key={t.tier} className="hover:bg-white/5">
                        <td className="border-b border-border-subtle px-3 py-3 text-sm font-semibold text-text-primary">
                          {t.tier}
                        </td>
                        <td className="border-b border-border-subtle px-3 py-3 text-sm text-text-secondary">
                          {t.maxPenalty}
                        </td>
                        <td className="border-b border-border-subtle px-3 py-3 text-sm text-text-secondary">
                          {t.keyViolations}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="bg-background-primary py-20" id="crossmap">
        <div className="container-ivalue">
          <ScrollReveal>
            <div className="glass-card p-6">
              <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">Cross-mapping</div>
              <div className="mt-3 text-sm text-text-secondary">
                DPDPA to RBI / SEBI / ISO 27701 / GDPR (seeded table).
              </div>

              <div className="mt-6 overflow-x-auto">
                <table className="min-w-[900px] w-full border-separate border-spacing-0">
                  <thead>
                    <tr>
                      {[
                        "DPDPA Control",
                        "RBI Mapping",
                        "SEBI Mapping",
                        "ISO 27701",
                        "GDPR Article",
                      ].map((h) => (
                        <th key={h} className="border-b border-border-subtle px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {DPDPA_CROSSMAP.map((r) => (
                      <tr key={r.dpdpaControl} className="hover:bg-white/5">
                        <td className="border-b border-border-subtle px-3 py-3 text-sm font-semibold text-text-primary">
                          {r.dpdpaControl}
                        </td>
                        <td className="border-b border-border-subtle px-3 py-3 text-sm text-text-secondary">{r.rbiMapping}</td>
                        <td className="border-b border-border-subtle px-3 py-3 text-sm text-text-secondary">{r.sebiMapping}</td>
                        <td className="border-b border-border-subtle px-3 py-3 text-sm text-text-secondary">{r.iso27701}</td>
                        <td className="border-b border-border-subtle px-3 py-3 text-sm text-text-secondary">{r.gdprArticle}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="bg-background-secondary py-20" id="privacy-tech">
        <div className="container-ivalue">
          <ScrollReveal>
            <div>
              <h2 className="text-3xl font-extrabold tracking-[-0.02em] text-text-primary md:text-5xl">Privacy technology stack</h2>
              <p className="mt-3 max-w-3xl text-lg text-text-secondary">
                A layered privacy architecture for consent, DSAR, data intelligence, protection, monitoring, and governance.
              </p>
            </div>
          </ScrollReveal>

          <div className="mt-10 grid gap-4">
            <div className="glass-card p-6">
              <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">Architecture overview</div>
              <div className="mt-4 grid gap-3 lg:grid-cols-2">
                {[
                  {
                    t: "Data Principal Interface",
                    d: "Privacy Center, Consent Portal, DSAR Portal, Preference Management",
                  },
                  {
                    t: "Privacy Orchestration",
                    d: "Privacy management platform: Consent, DSAR, Mapping, ROPA, Vendor privacy, DPIA",
                  },
                  {
                    t: "Data Protection",
                    d: "Masking, encryption, DLP, access controls",
                  },
                  {
                    t: "Security & Monitoring",
                    d: "SIEM/SOAR, DAM, UEBA, breach detection",
                  },
                  {
                    t: "Governance & Compliance",
                    d: "GRC, policy management, training/LMS, audit management",
                  },
                ].map((x) => (
                  <div key={x.t} className="rounded-2xl border border-border-subtle bg-white/5 p-5">
                    <div className="text-lg font-semibold text-text-primary">{x.t}</div>
                    <div className="mt-2 text-sm text-text-secondary">{x.d}</div>
                  </div>
                ))}
              </div>
            </div>

            {PRIVACY_TECH_CATEGORIES.map((cat) => (
              <div key={cat.title} className="glass-card p-6">
                <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">{cat.title}</div>
                <div className="mt-2 text-sm text-text-secondary">{cat.description}</div>

                <div className="mt-6 overflow-x-auto">
                  <table className="min-w-[900px] w-full border-separate border-spacing-0">
                    <thead>
                      <tr>
                        {[
                          "Vendor",
                          "Strengths",
                          "DPDPA Controls",
                          "Est. Cost",
                        ].map((h) => (
                          <th key={h} className="border-b border-border-subtle px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {cat.rows.map((r) => (
                        <tr key={r.vendor} className="hover:bg-white/5">
                          <td className="border-b border-border-subtle px-3 py-3 text-sm font-semibold text-text-primary">{r.vendor}</td>
                          <td className="border-b border-border-subtle px-3 py-3 text-sm text-text-secondary">{r.strengths}</td>
                          <td className="border-b border-border-subtle px-3 py-3 text-sm text-text-secondary">{r.dpdpaControls}</td>
                          <td className="border-b border-border-subtle px-3 py-3 text-sm text-text-secondary">{r.estimatedCost}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}

            <div className="glass-card p-6">
              <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">Recommended stack by organization size</div>
              <div className="mt-6 grid gap-3 lg:grid-cols-3">
                {RECOMMENDED_STACKS.map((s) => (
                  <div key={s.size} className="rounded-2xl border border-border-subtle bg-white/5 p-5">
                    <div className="text-lg font-semibold text-text-primary">{s.size}</div>
                    <div className="mt-4 space-y-2">
                      {s.bullets.map((b) => (
                        <div key={b} className="rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary">
                          {b}
                        </div>
                      ))}
                    </div>
                    <div className="mt-5 grid gap-2">
                      <div className="rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary">
                        Investment: {s.investment}
                      </div>
                      <div className="rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary">
                        Timeline: {s.timeline}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-primary py-20" id="journey">
        <div className="container-ivalue">
          <ScrollReveal>
            <div className="glass-card p-6">
              <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">DPDPA compliance journey</div>
              <div className="mt-6 grid gap-3 md:grid-cols-4">
                {[
                  {
                    t: "Month 1-2",
                    h: "Gap Assessment",
                    b: ["Data mapping", "Risk assessment", "Gap analysis", "Roadmap"],
                  },
                  {
                    t: "Month 3-4",
                    h: "Framework Design",
                    b: ["Policies", "Procedures", "ROPA", "DPA templates"],
                  },
                  {
                    t: "Month 5-8",
                    h: "Tech Deploy",
                    b: ["Privacy platform", "Consent mgmt", "DSAR automation", "Security controls"],
                  },
                  {
                    t: "Month 9-12",
                    h: "Operationalize",
                    b: ["Training", "Audit", "Monitoring", "Reporting"],
                  },
                ].map((x) => (
                  <div key={x.t} className="rounded-2xl border border-border-subtle bg-white/5 p-5">
                    <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">{x.t}</div>
                    <div className="mt-2 text-lg font-semibold text-text-primary">{x.h}</div>
                    <div className="mt-4 space-y-2">
                      {x.b.map((i) => (
                        <div key={i} className="rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary">
                          {i}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="bg-background-secondary py-20" id="cta">
        <div className="container-ivalue">
          <ScrollReveal>
            <div className="glass-card sheen p-8">
              <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">Next steps</div>
              <h2 className="mt-3 text-2xl font-extrabold tracking-[-0.02em] text-text-primary md:text-4xl">
                Start Your DPDPA Compliance Journey
              </h2>
              <p className="mt-3 max-w-2xl text-sm text-text-secondary">
                Choose the fastest path: baseline assessment, full catalog, tech stack consultation, or an expert workshop.
              </p>

              <div className="mt-8 grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                {[
                  { t: "DPDPA Readiness Assessment", d: "10 questions → instant score", href: "/assessment" },
                  { t: "Download full catalog", d: "PDF/Excel (placeholder)", href: "#dpdpa-catalog" },
                  { t: "Privacy tech stack consultation", d: "Build your target stack", href: "#privacy-tech" },
                  { t: "Book expert workshop", d: "2–4 hour working session", href: "/contact" },
                ].map((c) => (
                  <a key={c.t} href={c.href} className="rounded-2xl border border-border-subtle bg-white/5 p-5 transition-colors hover:bg-white/10">
                    <div className="text-sm font-semibold text-text-primary">{c.t}</div>
                    <div className="mt-2 text-sm text-text-secondary">{c.d}</div>
                    <div className="mt-4 text-sm font-semibold text-[color:var(--text-link)]">Open →</div>
                  </a>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
